import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const buildSnapOffsets = (itemCount, visibleCount, stride) => {
  if (!itemCount || !stride || itemCount <= visibleCount) return [0];

  const maxOffset = Math.max(0, (itemCount - visibleCount) * stride);
  const offsets = [0];
  let current = 0;

  while (current < maxOffset - 0.5) {
    const next = Math.min(current + visibleCount * stride, maxOffset);
    if (Math.abs(next - offsets[offsets.length - 1]) > 0.5) {
      offsets.push(next);
    }
    current = next;
  }

  return offsets;
};

const defaultMetrics = {
  visibleCount: 1,
  stride: 0,
  offsets: [0],
};

const readCardWidth = (track, card) => {
  const measured = card?.getBoundingClientRect().width ?? 0;
  if (measured > 0) return measured;

  const styles = getComputedStyle(track);
  const fromVar =
    styles.getPropertyValue("--course-card-width") ||
    styles.getPropertyValue("--recommended-card-width") ||
    styles.getPropertyValue("--topper-card-width") ||
    styles.getPropertyValue("--books-card-width") ||
    styles.getPropertyValue("--quick-action-card-width");

  const parsed = Number.parseFloat(fromVar);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export const useHorizontalCarousel = (
  itemCount = 0,
  resetKey = "",
  initialPageIndex = 0
) => {
  const viewportNodeRef = useRef(null);
  const trackNodeRef = useRef(null);
  const offsetsRef = useRef([0]);
  const measureRafRef = useRef(null);
  const normalizePageIndex = (value) =>
    Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  const [pageIndex, setPageIndex] = useState(() =>
    normalizePageIndex(initialPageIndex)
  );
  const [metrics, setMetrics] = useState(defaultMetrics);

  const recomputeMetrics = useCallback(() => {
    const viewport = viewportNodeRef.current;
    const track = trackNodeRef.current;

    if (!viewport || !track || itemCount <= 0) return;

    const cards = Array.from(track.children).filter(
      (node) => node.nodeType === Node.ELEMENT_NODE
    );

    const count = Math.max(itemCount, cards.length);
    if (!count) return;

    const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
    const cardWidth = readCardWidth(track, cards[0]);
    const viewportWidth = viewport.clientWidth;

    if (!cardWidth || !viewportWidth) return;

    const stride = cardWidth + gap;
    const visibleCount = Math.max(1, Math.floor((viewportWidth + gap) / stride));
    const offsets = buildSnapOffsets(count, visibleCount, stride);

    setMetrics((previous) => {
      const sameLength = previous.offsets.length === offsets.length;
      const sameValues =
        sameLength &&
        previous.offsets.every((value, index) => Math.abs(value - offsets[index]) < 1);

      if (sameValues && previous.visibleCount === visibleCount) {
        return previous;
      }

      return { visibleCount, stride, offsets };
    });
  }, [itemCount]);

  const scheduleMeasure = useCallback(() => {
    if (measureRafRef.current) {
      window.cancelAnimationFrame(measureRafRef.current);
    }

    measureRafRef.current = window.requestAnimationFrame(() => {
      recomputeMetrics();
      window.requestAnimationFrame(recomputeMetrics);
    });
  }, [recomputeMetrics]);

  const setViewportRef = useCallback(
    (node) => {
      viewportNodeRef.current = node;
      if (node) scheduleMeasure();
    },
    [scheduleMeasure]
  );

  const setTrackRef = useCallback(
    (node) => {
      trackNodeRef.current = node;
      if (node) scheduleMeasure();
    },
    [scheduleMeasure]
  );

  useEffect(() => {
    setPageIndex(normalizePageIndex(initialPageIndex));
  }, [resetKey, itemCount, initialPageIndex]);

  useEffect(() => {
    offsetsRef.current = metrics.offsets;
  }, [metrics.offsets]);

  useEffect(() => {
    setPageIndex((current) =>
      Math.min(current, Math.max(0, metrics.offsets.length - 1))
    );
  }, [metrics.offsets]);

  useLayoutEffect(() => {
    if (itemCount <= 0) return undefined;

    scheduleMeasure();

    const viewport = viewportNodeRef.current;
    const track = trackNodeRef.current;

    const handleResize = () => scheduleMeasure();
    window.addEventListener("resize", handleResize);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(handleResize);
      if (viewport) resizeObserver.observe(viewport);
      if (track) {
        resizeObserver.observe(track);
        Array.from(track.children).forEach((child) => resizeObserver.observe(child));
      }
    }

    let mutationObserver;
    if (typeof MutationObserver !== "undefined" && track) {
      mutationObserver = new MutationObserver(handleResize);
      mutationObserver.observe(track, { childList: true });
    }

    const timeoutId = window.setTimeout(scheduleMeasure, 500);

    return () => {
      window.clearTimeout(timeoutId);
      if (measureRafRef.current) {
        window.cancelAnimationFrame(measureRafRef.current);
      }
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [itemCount, resetKey, scheduleMeasure]);

  const scrollByPage = useCallback((direction) => {
    setPageIndex((current) => {
      const maxIndex = Math.max(0, offsetsRef.current.length - 1);
      return Math.max(0, Math.min(current + direction, maxIndex));
    });
  }, []);

  const trackOffset = Math.round(metrics.offsets[pageIndex] ?? 0);
  const hasOverflow = metrics.offsets.length > 1;
  const canScrollLeft = pageIndex > 0;
  const canScrollRight = pageIndex < metrics.offsets.length - 1;

  return {
    viewportRef: setViewportRef,
    trackRef: setTrackRef,
    trackOffset,
    pageIndex,
    pageCount: metrics.offsets.length,
    canScrollLeft,
    canScrollRight,
    hasOverflow,
    scrollByPage,
    recomputeMetrics,
  };
};
